import { FC } from 'react';
import { Form, message, Modal, Select, Button, Flex, Divider, Input, DatePicker, AutoComplete } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerRow } from '../useCustomers';
import { useEditCustomer } from './useEditCustomer';
import { filterOption } from '../../libs/form/filterOption';
import { getCountryOptions } from '../../libs/countries';
import dayjs, { Dayjs } from 'dayjs';
import { getCountyDivisionsAsSelectOption } from '../../libs/countries/divisions';

export const EditCustomerModal: FC<{
  customer: CustomerRow;
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose, customer }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const mutation = useEditCustomer();

  const [form] = Form.useForm();

  const onFinish = (values: {
    bridge_customer_id?: string;
    fuse_aed_primary_account_id?: string;
    fuse_usd_primary_account_id?: string;
    api_corridors?: string[];
    'address.country_alpha_2'?: string;
    'address.address_line_1'?: string;
    'address.city'?: string;
    'address.state'?: string;
    'address.postal_code'?: string;
    'identifying_information.dob'?: Dayjs;
    'identifying_information.tax_number'?: string;
    'identifying_information.tax_number_type'?: string;
    'identifying_information.id_document.number'?: string;
    'identifying_information.id_document.type'?: string;
    'identifying_information.id_document.issuing_country'?: string;
  }) => {
    mutation
      .mutateAsync({
        customerId: customer.id,
        payload: {
          bridge_customer_id: values.bridge_customer_id,
          fuse_aed_primary_account_id: values.fuse_aed_primary_account_id,
          fuse_usd_primary_account_id: values.fuse_usd_primary_account_id,
          api_corridors: values.api_corridors,
          address: {
            country_alpha_2: values['address.country_alpha_2'],
            city: values['address.city'],
            address_line_1: values['address.address_line_1'],
            postal_code: values['address.postal_code'],
            state: values['address.state'],
          },
          identifying_information: {
            dob: values['identifying_information.dob']
              ? values['identifying_information.dob'].format('YYYY-MM-DD')
              : undefined,
            id_document: {
              number: values['identifying_information.id_document.number'],
              type: values['identifying_information.id_document.type'],
              issuing_country: values['identifying_information.id_document.issuing_country'],
            },
            tax_number: values['identifying_information.tax_number'],
            tax_number_type: values['identifying_information.tax_number_type'],
          },
        },
      })
      .then(() => {
        messageApi.success('Successfully edited!');
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['customers'],
        });
      })
      .catch((err) => {
        messageApi.error(
          err?.response?.status === 424 ? `Error: ${err?.response?.data?.error}` : 'Something is wrong',
          6000
        );
      });
  };

  return (
    <>
      {contextHolder}
      <Modal
        loading={mutation.isPending}
        title="Edit Customer Details"
        destroyOnClose
        footer={null}
        open={open}
        onCancel={onClose}
      >
        <Form
          onFinish={onFinish}
          name="editCustomer"
          form={form}
          initialValues={{
            bridge_customer_id: customer.money_movement_settings?.implementer_contexts?.bridge?.context.customerId,
            fuse_aed_primary_account_id:
              customer.money_movement_settings?.implementer_contexts?.fuse?.context?.primaryAccounts?.aed?.id,
            fuse_usd_primary_account_id:
              customer.money_movement_settings?.implementer_contexts?.fuse?.context?.primaryAccounts?.usd?.id,
            'address.country_alpha_2': customer.address?.country_alpha_2,
            'address.address_line_1': customer.address?.address_line_1,
            'address.city': customer.address?.city,
            api_corridors: customer.money_movement_settings?.core_api?.enabled_corridors || [],
            'address.state': customer.address?.state,
            'address.postal_code': customer.address?.postal_code,

            'identifying_information.dob': customer.identifying_information?.dob
              ? dayjs(customer.identifying_information.dob)
              : undefined,
            'identifying_information.tax_number': customer.identifying_information?.tax_number,
            'identifying_information.tax_number_type': customer.identifying_information?.tax_number_type,
            'identifying_information.id_document.number': customer.identifying_information?.id_document?.number,
            'identifying_information.id_document.type': customer.identifying_information?.id_document?.type,
            'identifying_information.id_document.issuing_country':
              customer.identifying_information?.id_document?.issuing_country,
          }}
          requiredMark={false}
          style={{
            paddingTop: 20,
          }}
          layout="vertical"
          autoComplete="off"
        >
          <Divider variant="dashed">Address</Divider>
          <Form.Item name="address.country_alpha_2" layout="vertical" label="Country">
            <Select
              showSearch
              placeholder={'Select country'}
              filterOption={filterOption}
              options={getCountryOptions()}
            />
          </Form.Item>

          <Flex gap="middle">
            <Form.Item style={{ flex: 1 }} name="address.address_line_1" layout="vertical" label="Street Line">
              <Input />
            </Form.Item>

            <Form.Item style={{ flex: 1 }} name="address.city" layout="vertical" label="City">
              <Input />
            </Form.Item>
          </Flex>

          <Flex gap="middle">
            <Form.Item style={{ flex: 1 }} name="address.state" layout="vertical" label="State">
              <AutoComplete
                showSearch
                allowClear
                placeholder="Select or type a state"
                filterOption={filterOption}
                options={
                  form.getFieldValue('address.country_alpha_2') || customer.address?.country_alpha_2
                    ? getCountyDivisionsAsSelectOption(
                        form.getFieldValue('address.country_alpha_2') || customer.address?.country_alpha_2
                      )
                    : []
                }
              />
            </Form.Item>

            <Form.Item style={{ flex: 1 }} name="address.postal_code" layout="vertical" label="Postal Code">
              <Input />
            </Form.Item>
          </Flex>

          <Divider variant="dashed">Identifying Information</Divider>

          <Flex gap="middle">
            <Form.Item style={{ flex: 1 }} name="identifying_information.dob" layout="vertical" label="Date Of Birth">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="identifying_information.tax_number"
              layout="vertical"
              label="Tax Number"
            >
              <Input />
            </Form.Item>
          </Flex>

          <Flex gap="middle">
            <Form.Item
              style={{ flex: 1 }}
              name="identifying_information.tax_number_type"
              layout="vertical"
              label="Tax Number Type"
            >
              <Select
                placeholder="Select tax number type"
                options={[
                  { value: 'ssn', label: 'SSN (US)' },
                  { value: 'tckn', label: 'TCKN (TUR)' },
                  { value: 'nif', label: 'NIF (PRT, ESP)' },
                  { value: 'nie', label: 'NIE (ESP)' },
                  { value: 'tin', label: 'TIN (SWE, HUN)' },
                  { value: 'steuer_id', label: 'Steuer ID (DE)' },
                  { value: 'cf', label: 'Codice Fiscale (Tax Code) (ITA)' },
                  { value: 'cpf', label: 'CPF (BRA)' },
                  { value: 'jmbg', label: 'JMBG (SRB)' },
                  { value: 'pesel', label: 'PESEL (POL)' },
                  { value: 'ppsn', label: 'PPSN (IRL)' },
                  { value: 'nino', label: 'NINO (GBR)' },
                  { value: 'spi', label: 'SPI (FRA)' },
                  { value: 'cpr', label: 'CPR (DNK)' },
                  { value: 'rut', label: 'RUT (CHL, COL)' },
                  { value: 'rfc', label: 'MEX (MEX)' },
                  { value: 'emirates_id', label: 'EMIRATES ID (ARE)' },
                ]}
              />
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="identifying_information.id_document.type"
              layout="vertical"
              label="Id Doc Type"
            >
              <Select
                placeholder="Select ID document type"
                options={[
                  { value: 'permanent_residency_id', label: 'Permanent Residency ID' },
                  { value: 'state_or_provincial_id', label: 'State or Provincial ID' },
                  { value: 'national_id', label: 'National ID' },
                  { value: 'drivers_license', label: "Driver's License" },
                  { value: 'passport', label: 'Passport' },
                ]}
              />
            </Form.Item>
          </Flex>

          <Flex gap="middle">
            <Form.Item
              style={{ flex: 1 }}
              name="identifying_information.id_document.number"
              layout="vertical"
              label="Id Doc Number"
            >
              <Input />
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="identifying_information.id_document.issuing_country"
              layout="vertical"
              label="Id Doc Issuing Country"
            >
              <Select
                showSearch
                placeholder={'Select country'}
                filterOption={filterOption}
                options={getCountryOptions()}
              />
            </Form.Item>
          </Flex>

          <Divider variant="dashed">Bridge</Divider>

          <Form.Item name="bridge_customer_id" layout="vertical" label="Bridge Customer Id">
            <Input />
          </Form.Item>

          <Divider variant="dashed">Fuse</Divider>

          <Form.Item name="fuse_aed_primary_account_id" layout="vertical" label="AED Primary Account Id">
            <Input />
          </Form.Item>

          <Form.Item name="fuse_usd_primary_account_id" layout="vertical" label="USD Primary Account Id">
            <Input />
          </Form.Item>

          <Divider variant="dashed">Fees And Settings</Divider>

          <Form.Item name="api_corridors" layout="vertical" label="Enabled Api Corridors">
            <Select
              mode="multiple"
              placeholder="Select"
              options={[
                { value: 'align', label: 'Align' },
                { value: 'bridge', label: 'Bridge' },
              ]}
            />
          </Form.Item>

          <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Edit
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
